import { SpaceOffering } from "../offering/api";
import { isPartnerLocation } from "./api";
import { PartnerCatalog } from "./catalog";
import { ImageVersion, ImageData } from "./image";
import { Location } from "./location";

export enum TemplateName {
  DayPass = "DayPass",
  MultiPass = "MultiPass",
  EmbedDayPass = "EmbedDayPass",
  EmbedDayOffice = "EmbedDayOffice",
  EmbedMeetingRoom = "EmbedMeetingRoom",
  MultiDayOffice = "MultiDayOffice",
  Membership = "Membership",
}

export enum Template {
  DayPass = "day_pass",
  MultiPass = "multi_pass",
  AddOn = "add_on",
  DailyAccess = "daily_access",
  DayOfficeMultiPass = "day_office_multi_pass",
  DayOffice = "day_office",
  MeetingRoom = "meeting_room",
  SnowflakeDayOfficeListing = "snowflake_day_office_listing",
  SnowflakeDayOffice = "snowflake_day_office",

  // V2 templates
  DailyOffice = "daily_office",
  DailyAccessV2 = "daily_access_v2",
  MeetingRoomV2 = "meeting_room_v2",
  AddOnV2 = "add_on_v2",
  Membership = "membership_v2",
  EventSpace = "event_space_v2",
  RoomGallery = "room_gallery_v2",
}

export enum SortOrder {
  HighestToLowest = "HighestToLowest",
  LowestToHighest = "LowestToHighest",
}

enum Criterion {
  Capacity = "capacity",
  Location = "location",
  City = "city",
  Datetime = "datetime",
  State = "state",
  Layout = "layout",
  ProductCategory = "product_category",
}

interface CapacityOption {
  label: string;
  value: { minCapacity?: number; maxCapacity?: number };
}

export interface GalleryFilterConfig {
  city?: boolean;
  location?: boolean;
  capacity?: boolean;
  datetime?: boolean;
  state?: boolean;
  layout?: boolean;
  capacityOptions?: CapacityOption[];
  defaultSortOrder?: SortOrder;
  productCategory?: boolean;
}

export enum ProductCategory {
  MeetingRoom = "meeting_room",
  PrivateOffice = "private_office",
  EventSpace = "event_space",
}

export function hasMoreThanOneProductCategory(
  locations: PartnerOfferingLocation[] = []
): boolean {
  return (
    new Set(locations.map((l) => l.productCategory).filter(Boolean)).size > 1
  );
}

export interface PartnerOffering {
  id: string;
  name: string;
  template: Template;
  offeringTitle?: string;
  description: string;
  partner: Partner;
  addOns: SpaceOffering[];
  offeringLocations: PartnerOfferingLocation[];
  templateName: TemplateName;
  promoCodeBoxContent?: string;
  generalInfo?: string;
  showDiscountLabel: boolean;
  faqs?: string;
  slug: string;
  usePartnerOfferingImagesAcrossLocations: boolean;
  cancellationPolicy?: string;
  allowSelectableStartDate: boolean;
  singleDaySelect?: boolean;
}

export type LightPartnerOffering = Pick<
  PartnerOffering,
  | "id"
  | "name"
  | "template"
  | "offeringTitle"
  | "slug"
  | "promoCodeBoxContent"
  | "description"
  | "generalInfo"
  | "showDiscountLabel"
  | "faqs"
  | "partner"
>;

export interface Products {
  partner: Partner;
  spaceLocations: PartnerOfferingLocation[];
  addOns: SpaceOffering[];
  templateName: TemplateName;
}

export interface Partner {
  id: string;
  name: string;
  partnerSlug: string;
  homepageUrl: string;
  logoUrl: string;
  internalName: string;
}

export interface PartnerOfferingLocation {
  id: string;
  spaceID: string;
  integrationProductID: string;
  location: Location;
  locationDisplay: string;
  promoCodeBoxContent?: string;
  featuredPromoDescription?: string;
  generalInfo?: string;
  faqs?: string;
  offeringItemType?: OfferingItemType;
  membership?: MembershipDetail;
  spaceMaxCapacity: number;
  productCategory?: ProductCategory;
}

export interface FilterLocation {
  id: string;
  locationDisplay: string;
  city: string;
  state: string;
}

export function toFilterLocationFromOfferingLocation(
  partnerOfferingLocation: PartnerOfferingLocation
): FilterLocation {
  return {
    id: partnerOfferingLocation.location.id,
    locationDisplay:
      partnerOfferingLocation.locationDisplay ||
      partnerOfferingLocation.location.name,
    city: partnerOfferingLocation.location.city,
    state: partnerOfferingLocation.location.state,
  };
}

export function toFilterLocationFromLocation(
  location: Location
): FilterLocation {
  return {
    id: location.id,
    locationDisplay: location.locationDisplay || location.name,
    city: location.city,
    state: location.state,
  };
}

export function getCatalogFilterLocations(
  locations: FilterLocation[],
  catalog: PartnerCatalog
) {
  return locations?.filter((location) =>
    catalog.partnerCatalogCategories
      .flatMap((category) =>
        category.catalogOfferings?.flatMap((offering) => offering.locationIDs)
      )
      .includes(location.id)
  );
}

export type OfferingItemType = "Location" | "Space" | "Membership";

export type LocatableType = "Location" | "GeoMarket" | "SpacePartner";

export interface GeoMarket {
  id: string;
  name: string;
  description: string;
  locations: Location[];
}

export interface MembershipDetail {
  currency: string;
  description: string;
  id: string;
  locatable?: Location | GeoMarket;
  locatableType: LocatableType;
  monthlyPrice: number;
  name: string;
  partnerId: string;
  stripeProductId: string;
  timeZone: string;
  yearlyPrice: number;
  firstPaymentCycles: number;
  images: ImageVersion[];
  inventory?: number;
  productCategory: string;
  productTypeName: string;
  recurring?: boolean;
}

export interface OfferingMenu {
  showOfferingMenu: boolean;
  offerings: Offering[];
}

export interface Offering {
  id: string;
  slug: string;
  menuDisplayName: string;
}

export interface PartnerOfferingFilterConfig {
  criterion: string;
  enabled: boolean;
}

function toImageData(data: any): ImageData {
  return {
    url: data.url,
    width: data.width,
    height: data.height,
  };
}

export function toImage(data: any): ImageVersion {
  return {
    original: toImageData(data.original),
    thumb: toImageData(data.thumb),
    small: toImageData(data.small),
    medium: toImageData(data.medium),
    large: toImageData(data.large),
    xlarge: toImageData(data.xlarge),
  };
}

export function toPartnerOfferingFilterConfig(
  data: any
): PartnerOfferingFilterConfig {
  return {
    criterion: data.criterion,
    enabled: data.enabled,
  };
}

export function getPartnerOfferingFilterConfig(
  partnerOfferingFilterConfigs: PartnerOfferingFilterConfig[],
  template: Template
): GalleryFilterConfig {
  switch (template) {
    case Template.RoomGallery:
      return getRoomGalleryFilterConfig(partnerOfferingFilterConfigs);
    case Template.MeetingRoomV2:
    case Template.EventSpace:
      return getMeetingRoomFilterConfig(partnerOfferingFilterConfigs);
    default:
      return getMeetingRoomFilterConfig(partnerOfferingFilterConfigs);
  }
}

export function getMeetingRoomFilterConfig(
  partnerOfferingFilterConfigs: PartnerOfferingFilterConfig[]
) {
  let enabledLocation = false;
  let enabledCapacity = false;
  let enabledCity = false;
  let enabledDatetime = false;
  let enabledState = false;
  let enabledLayout = false;
  partnerOfferingFilterConfigs.forEach((p) => {
    if (p.criterion === Criterion.Capacity) {
      enabledCapacity = p.enabled;
    }
    if (p.criterion === Criterion.Location) {
      enabledLocation = p.enabled;
    }
    if (p.criterion === Criterion.City) {
      enabledCity = p.enabled;
    }
    if (p.criterion === Criterion.Datetime) {
      enabledDatetime = p.enabled;
    }
    if (p.criterion === Criterion.State) {
      enabledState = p.enabled;
    }
    if (p.criterion === Criterion.Layout) {
      enabledLayout = p.enabled;
    }
  });
  return {
    location: enabledLocation,
    capacity: enabledCapacity,
    city: enabledCity,
    datetime: enabledDatetime,
    state: enabledState,
    layout: enabledLayout,
  };
}

function getRoomGalleryFilterConfig(
  partnerOfferingFilterConfigs: PartnerOfferingFilterConfig[]
): GalleryFilterConfig {
  let enabledLocation = true;
  let enabledCapacity = true;
  let enabledCity = true;
  let enabledDatetime = true;
  let enabledState = true;
  let enabledLayout = true;
  let enabledProductCategory = true;

  partnerOfferingFilterConfigs.forEach((p) => {
    if (p.criterion === Criterion.ProductCategory) {
      enabledProductCategory = p.enabled;
    }
    if (p.criterion === Criterion.Capacity) {
      enabledCapacity = p.enabled;
    }
    if (p.criterion === Criterion.Location) {
      enabledLocation = p.enabled;
    }
    if (p.criterion === Criterion.City) {
      enabledCity = p.enabled;
    }
    if (p.criterion === Criterion.Datetime) {
      enabledDatetime = p.enabled;
    }
    if (p.criterion === Criterion.State) {
      enabledState = p.enabled;
    }
    if (p.criterion === Criterion.Layout) {
      enabledLayout = p.enabled;
    }
  });
  return {
    location: enabledLocation,
    capacity: enabledCapacity,
    city: enabledCity,
    datetime: enabledDatetime,
    state: enabledState,
    layout: enabledLayout,
    productCategory: enabledProductCategory,
  };
}

export function calculateDiscountPercentage(
  multiPass: SpaceOffering,
  singlePass?: SpaceOffering
): number {
  if (!singlePass) return 0;

  if (singlePass.addOnID === multiPass.addOnID) {
    return 0;
  }
  if (!multiPass.numberOfEntries) return 0;

  const originPrice = multiPass.numberOfEntries * singlePass.price;
  const discountedPrice = multiPass.price;
  const discountValue = (originPrice - discountedPrice) / originPrice;

  return Math.round(discountValue * 100);
}

export function toPartnerOfferingImage(data: any): ImageVersion {
  return toImage(data);
}

export function getMembershipInfoFromLocation(
  offeringLocations: PartnerOfferingLocation[],
  locationID?: string
): MembershipDetail | undefined {
  if (!offeringLocations) {
    return undefined;
  }

  let selectedMembership;
  offeringLocations.forEach((loc) => {
    const membership = loc.membership;

    if (isPartnerLocation(membership?.locatable)) {
      if (membership?.locatable.id === locationID) {
        selectedMembership = membership;
      }
    }
  });

  if (!selectedMembership) {
    return (
      offeringLocations.find(
        (loc) => loc.membership?.locatableType === "Location"
      )?.membership || offeringLocations[0].membership
    );
  }

  return selectedMembership;
}

export function getMembershipOfferingLocationFromLocationId(
  offeringLocations: PartnerOfferingLocation[],
  locationID?: string
): PartnerOfferingLocation | undefined {
  if (!offeringLocations) {
    return undefined;
  }

  let selectedOfferingLocation;
  offeringLocations.forEach((loc) => {
    const membership = loc.membership;
    if (isPartnerLocation(membership?.locatable)) {
      if (membership?.locatable.id === locationID) {
        selectedOfferingLocation = loc;
      }
    } else {
      if (
        membership?.locatable?.locations?.some((loc) => loc.id === locationID)
      ) {
        selectedOfferingLocation = loc;
      }
    }
  });

  if (!selectedOfferingLocation) {
    return offeringLocations[0];
  }

  return selectedOfferingLocation;
}

export function hasFoundOfferingFromQueryLocationId(
  offeringLocations?: PartnerOfferingLocation[],
  queryLocationId?: string
) {
  return offeringLocations?.some(
    (loc) =>
      isPartnerLocation(loc.membership?.locatable) &&
      loc.membership?.locatable?.id === queryLocationId
  );
}

export function shouldHideLocationAndCityPicker(
  offeringLocations?: PartnerOfferingLocation[]
) {
  return (
    offeringLocations?.length === 1 &&
    isGeoMarketOrSpacePartner(offeringLocations)
  );
}

export function isGeoMarketOrSpacePartner(
  offeringLocations?: PartnerOfferingLocation[]
) {
  return !!offeringLocations?.some(
    (loc) =>
      loc.membership?.locatableType === "GeoMarket" ||
      loc.membership?.locatableType === "SpacePartner"
  );
}

export const findIsSoldOut = (
  anyLocations: Location[],
  offeringLocations?: PartnerOfferingLocation[]
) => {
  if (!offeringLocations) {
    return false;
  }

  if (!anyLocations.length) {
    return offeringLocations.every(
      (offLoc) => offLoc.membership?.inventory === 0
    );
  }

  return anyLocations.every((anyLoc) => {
    const offeringLocation = offeringLocations.find((loc) => {
      if (isPartnerLocation(loc.membership?.locatable)) {
        return loc.membership?.locatable.id === anyLoc.id;
      }

      return false;
    });

    return offeringLocation && offeringLocation.membership?.inventory === 0;
  });
};

export const getFirstAvailableLocation = (
  locations: Location[],
  offeringLocations: PartnerOfferingLocation[]
) => {
  return locations.find((loc) => {
    const membership = getMembershipInfoFromLocation(offeringLocations, loc.id);

    return membership?.inventory !== 0;
  });
};

export const getMembershipLocations = (
  offeringLocations: PartnerOfferingLocation[]
) => {
  const locations: Location[] = [];

  offeringLocations.forEach((location) => {
    if (
      location.membership?.locatable &&
      isPartnerLocation(location.membership?.locatable)
    ) {
      const foundLocation = location.membership.locatable;
      locations.push({
        ...foundLocation,
        name: location.locationDisplay || foundLocation.name,
      });
    }
  });

  return locations;
};
